import { Compass, HelpCircle, Target, Clock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface XiaBarProps {
  /** Qué hacemos ahora (actividad en curso). */
  que: string;
  /** Por qué lo hacemos (propósito de la técnica). */
  porQue: string;
  /** Hacia dónde va (qué produce / siguiente eslabón de la cadena). */
  hacia: string;
  /** Cuánto tiempo queda, ya formateado (ej. "08:30"). */
  tiempoRestante: string;
}

interface XiaItem {
  icon: LucideIcon;
  etiqueta: string;
  valor: string;
}

/**
 * Barra XIA — orientación persistente del North Star: qué hacemos, por qué,
 * hacia dónde va y cuánto tiempo queda. Presentacional (sin estado propio);
 * la superficie que la monta le pasa los textos del manifiesto de la
 * herramienta activa. Se usa como landmark de navegación global.
 */
export function XiaBar({ que, porQue, hacia, tiempoRestante }: XiaBarProps) {
  const items: XiaItem[] = [
    { icon: Compass, etiqueta: 'Qué hacemos', valor: que },
    { icon: HelpCircle, etiqueta: 'Por qué', valor: porQue },
    { icon: Target, etiqueta: 'Hacia dónde va', valor: hacia },
  ];

  return (
    <nav
      aria-label="Orientación de la sesión (XIA)"
      className="w-full rounded-lg border border-border bg-surface-raised px-4 py-3 shadow-sm"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-6">
        {items.map(({ icon: Icon, etiqueta, valor }) => (
          <div key={etiqueta} className="flex items-start gap-2 sm:flex-1">
            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
                {etiqueta}
              </p>
              <p className="font-sans text-sm text-content">{valor}</p>
            </div>
          </div>
        ))}

        <div className="flex items-center gap-2 border-border sm:border-l sm:pl-6">
          <Clock className="h-5 w-5 shrink-0 text-cyan" aria-hidden="true" />
          <div>
            <p className="font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
              Tiempo restante
            </p>
            <p
              className="font-display text-lg font-semibold tabular-nums text-content"
              aria-label={`Tiempo restante ${tiempoRestante}`}
            >
              {tiempoRestante}
            </p>
          </div>
        </div>
      </div>
    </nav>
  );
}
